import { useCallback, useEffect, useMemo, useState } from 'react';

import { ActionSheet, YStack, createMqStyles, tokens } from '@fhs/ui';

import { useAddGiftCardMutation } from '../../mutations/use-add-gift-card';
import { useGetBalanceQuery } from '../../queries/use-get-balance';
import { GiftCardsActionSheetHeader } from '../gift-cards-action-sheet-header';

import { StepAddCardForm } from './step-add-card-form';
import { StepCardSummary } from './step-card-summary';
import { StepCheckBalance } from './step-check-balance';
import { GiftCardActionSheetProps, Step } from './types';

export const GiftCardActionSheet = ({
  headerTitle,
  isVisible,
  isCheckBalanceFlow = false,
  currentStep = Step.ADD_CARD_FORM,
  currentDigitalBalance = 0,
  handleBalanceUpdate = () => {},
  handleError = () => {},
  setCurrentStep,
  onClose = () => {},
  onGiftCardSaved,
}: GiftCardActionSheetProps) => {
  const [cardId, setCardId] = useState('');
  const [pin, setPin] = useState('');
  const [isLoadingGiftCard, setIsLoadingGiftCard] = useState(false);
  const [giftCardBalance, setGiftCardBalance] = useState(0);
  const [balanceErrorMessage, setBalanceErrorMessage] = useState('');

  const mqStyles = useMqStyles();

  const { mutate: saveGiftCard, isPending: isSavingGiftCard } = useAddGiftCardMutation({
    onAddGiftCard: (newBalance: number) => {
      handleBalanceUpdate(newBalance);
      if (onGiftCardSaved) {
        onGiftCardSaved();
      } else {
        onClose();
      }
    },
    onError: () => {
      handleError();
      onClose();
    },
  });
  const { data, isPending: balanceIsPending, isError } = useGetBalanceQuery({
    token: cardId,
    pin,
    expiryDate: '2512', // temporary hardcoded expiry date, we don't have it available in the current flow
    isPhysicalCardBalanceCheck: true,
    fetchBalance: isLoadingGiftCard,
  });

  const handleBackNavigation = useCallback(() => {
    setCurrentStep(prev => {
      switch (prev) {
        case Step.CHECK_BALANCE:
          return Step.ADD_CARD_FORM;

        case Step.CARD_SUMMARY:
          return isCheckBalanceFlow ? Step.CHECK_BALANCE : Step.ADD_CARD_FORM;

        case Step.ADD_CARD_FORM:
        default:
          return prev; // nothing to go back to
      }
    });
  }, [setCurrentStep, isCheckBalanceFlow]);

  const handleChangePress = useCallback(() => {
    setCurrentStep(Step.ADD_CARD_FORM);
  }, [setCurrentStep]);

  const handleAddCardContinue = useCallback(() => {
    setBalanceErrorMessage('');
    setIsLoadingGiftCard(true);
  }, []);

  const handleCardIdChange = useCallback((newCardId: string) => {
    setBalanceErrorMessage('');
    setCardId(newCardId);
  }, []);

  const handlePinChange = useCallback((newPin: string) => {
    setBalanceErrorMessage('');
    setPin(newPin);
  }, []);

  useEffect(() => {
    // Handle error case - reset state and notify parent
    if (isError && isLoadingGiftCard) {
      setGiftCardBalance(0);
      setIsLoadingGiftCard(false);
      setBalanceErrorMessage('There was an error checking your gift card balance. Please verify the card details and try again.');
      handleError();
      return;
    }

    // move to next step when we have the balance loaded (only if we triggered the query)
    if (isLoadingGiftCard && !balanceIsPending && data?.currentBalance) {
      setGiftCardBalance(data.currentBalance);
      setIsLoadingGiftCard(false);
      setBalanceErrorMessage('');

      if (isCheckBalanceFlow) {
        setCurrentStep(Step.CHECK_BALANCE);
        return;
      }

      setCurrentStep(Step.CARD_SUMMARY);
    }
  }, [
    balanceIsPending,
    data?.currentBalance,
    isCheckBalanceFlow,
    isError,
    isLoadingGiftCard,
    handleError,
    setCurrentStep,
  ]);

  const handleSaveCard = useCallback(() => {
    saveGiftCard({ cardNumber: cardId, pin });
  }, [saveGiftCard, cardId, pin]);

  const stepContent = useMemo(() => {
    switch (currentStep) {
      case Step.ADD_CARD_FORM:
        return (
          <StepAddCardForm
            cardId={cardId}
            onCardIdChange={handleCardIdChange}
            pin={pin}
            onPinChange={handlePinChange}
            isLoadingGiftCard={isLoadingGiftCard}
            onContinue={handleAddCardContinue}
            errorMessage={balanceErrorMessage}
          />
        );
      case Step.CARD_SUMMARY:
        return (
          <StepCardSummary
            cardId={cardId}
            onChangePress={handleChangePress}
            onSave={handleSaveCard}
            isLoading={isSavingGiftCard}
            balance={giftCardBalance}
            digitalBalance={currentDigitalBalance}
          />
        );
      case Step.CHECK_BALANCE:
        return (
          <StepCheckBalance
            cardId={cardId}
            onChangePress={handleChangePress}
            onAddToYourAccountPress={() => setCurrentStep(Step.CARD_SUMMARY)}
            balance={giftCardBalance}
          />
        );
    }
  }, [
    currentStep,
    cardId,
    pin,
    isLoadingGiftCard,
    handleAddCardContinue,
    handleCardIdChange,
    handlePinChange,
    handleChangePress,
    handleSaveCard,
    isSavingGiftCard,
    giftCardBalance,
    currentDigitalBalance,
    balanceErrorMessage,
    setCurrentStep,
  ]);

  return (
    <ActionSheet
      onRequestClose={onClose}
      onClose={onClose}
      isVisible={isVisible}
      grabber={false}
      noPanGestureDetection
      containerStyle={{ minHeight: '30%' }}
      drawerProps={{
        style: { flex: 1, backgroundColor: tokens.colors.$white, borderRadius: 12 },
      }} // Needed to make the flat list scroll inside the actionsheet
    >
      <GiftCardsActionSheetHeader
        headerTitle={headerTitle}
        onClose={onClose}
        onPressPrev={handleBackNavigation}
        disableBackButton={currentStep === Step.ADD_CARD_FORM}
      />
      <YStack style={mqStyles.contentWrapper}>{stepContent}</YStack>
    </ActionSheet>
  );
};

const useMqStyles = createMqStyles({
  contentWrapper: {
    $base: {
      padding: 16,
    },
    $gteDesktopLg: {
      paddingHorizontal: 28,
    },
  },
});
